import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { PageHeader, Icon, Tabs, TabsPanel, BrandBand } from '@salesforce/design-system-react';
import TransactionHistory from './transaction_history';
import Overview from './overview';
import Assets from './assets';
import Protocol from './protocol';
import Chain from './chains';
import DetailsTab from './detailsTab';
import { useDispatch, useSelector } from 'react-redux';
import { getNumbersWithDecimalPlaces, getNumberWithCommas } from '../../../utilities/money';
import {
  defiOverviewSelector,
  getDefiPerformanceSummaryByAccount,
} from '../../../store/defi/overview';

const tabHierarchy = ['overview', 'assets', 'protocol', 'transactions', 'chains', 'details'];

export default function Details() {
  const dispatch = useDispatch();
  const { account_performance_summary } = useSelector(defiOverviewSelector);

  const navigate = useNavigate();
  const location = useLocation();
  const [activeKey, setActiveKey] = useState(0);
  const [account, setAccount] = useState(undefined);

  useEffect(() => {
    if (account) {
      dispatch(getDefiPerformanceSummaryByAccount(account));
    }
  }, [dispatch, account]);

  const setActiveTab = (selectedIndex) => {
    const path = location.pathname.split('/').filter((path) => path !== '');
    navigate(`/defi/${path[1]}/${tabHierarchy[selectedIndex]}`);
    setActiveKey(selectedIndex);
  };

  const handleNavigation = (key) => {
    setActiveKey(key);
  };

  useEffect(() => {
    /**
     * URI will be in the format /defi/:account/path
     */
    const path = location.pathname.split('/').filter((path) => path !== '');

    //This will set the current account id in the state
    setAccount(path[1]);
    //this will find out the active tab in the page
    let index = tabHierarchy.indexOf(path[2]);
    if (index !== -1) {
      setActiveKey(index);
    } else {
      setActiveKey(0);
    }
  }, [location, setActiveKey]);

  const getAmountStyleColor = (amount) => {
    if (!Number(amount)) return '';

    return Number(amount) > 0 ? '#54A77B' : '#e16032';
  };

  const details = account_performance_summary
    ? [
        {
          label: 'Total Balance',
          content: `${
            account_performance_summary?.user_current_total_balance
              ? '$ ' +
                getNumberWithCommas(
                  getNumbersWithDecimalPlaces(
                    account_performance_summary?.user_current_total_balance?.amount
                  )
                )
              : '--'
          }`,
          truncate: true,
        },
        {
          label: 'Liquid Balance',
          content: `${
            account_performance_summary?.liquid_balance
              ? '$ ' +
                getNumberWithCommas(
                  getNumbersWithDecimalPlaces(account_performance_summary?.liquid_balance)
                )
              : '--'
          }`,
        },
        {
          label: 'Staked Balance',
          content: `${
            account_performance_summary?.staked_balance
              ? '$ ' +
                getNumberWithCommas(
                  getNumbersWithDecimalPlaces(account_performance_summary?.staked_balance)
                )
              : '--'
          }`,
        },
        {
          label: '24 hour change',
          content: (
            <span
              style={{
                color: getAmountStyleColor(
                  account_performance_summary?.user_total_balance_a_day_ago?.amount_change
                ),
              }}
            >
              {}
              {account_performance_summary?.user_total_balance_a_day_ago?.amount_change
                ? '$ ' +
                  getNumberWithCommas(
                    getNumbersWithDecimalPlaces(
                      account_performance_summary?.user_total_balance_a_day_ago?.amount_change
                    )
                  )
                : '--'}{' '}
              {account_performance_summary?.user_total_balance_a_day_ago?.percentage_change
                ? '(' +
                  getNumbersWithDecimalPlaces(
                    account_performance_summary?.user_total_balance_a_day_ago?.percentage_change
                  ) +
                  '%)'
                : '--'}
            </span>
          ),
        },
        {
          label: 'Week Change',
          content: (
            <span
              style={{
                color: getAmountStyleColor(
                  account_performance_summary?.user_total_balance_a_week_ago?.amount_change
                ),
              }}
            >
              {}
              {account_performance_summary?.user_total_balance_a_week_ago?.amount_change
                ? '$ ' +
                  getNumberWithCommas(
                    getNumbersWithDecimalPlaces(
                      account_performance_summary?.user_total_balance_a_week_ago?.amount_change
                    )
                  )
                : '--'}{' '}
              {account_performance_summary?.user_total_balance_a_week_ago?.percentage_change
                ? '(' +
                  getNumbersWithDecimalPlaces(
                    account_performance_summary?.user_total_balance_a_week_ago?.percentage_change
                  ) +
                  '%)'
                : '--'}
            </span>
          ),
        },
        {
          label: 'Month Change',
          content: (
            <span
              style={{
                color: getAmountStyleColor(
                  account_performance_summary?.user_total_balance_a_month_ago?.amount_change
                ),
              }}
            >
              {}
              {account_performance_summary?.user_total_balance_a_month_ago?.amount_change
                ? '$ ' +
                  getNumberWithCommas(
                    getNumbersWithDecimalPlaces(
                      account_performance_summary?.user_total_balance_a_month_ago?.amount_change
                    )
                  )
                : '--'}{' '}
              {account_performance_summary?.user_total_balance_a_month_ago?.percentage_change
                ? '(' +
                  getNumbersWithDecimalPlaces(
                    account_performance_summary?.user_total_balance_a_month_ago?.percentage_change
                  ) +
                  '%)'
                : '--'}
            </span>
          ),
        },
        {
          label: 'YTD Change',
          content: (
            <span
              style={{
                color: getAmountStyleColor(
                  account_performance_summary?.user_total_balance_a_year_ago?.amount_change
                ),
              }}
            >
              {}
              {account_performance_summary?.user_total_balance_a_year_ago?.amount_change
                ? '$ ' +
                  getNumberWithCommas(
                    getNumbersWithDecimalPlaces(
                      account_performance_summary?.user_total_balance_a_year_ago?.amount_change
                    )
                  )
                : '--'}{' '}
              {account_performance_summary?.user_total_balance_a_year_ago?.percentage_change ===
              '--'
                ? '(' +
                  getNumbersWithDecimalPlaces(
                    account_performance_summary?.user_total_balance_a_year_ago?.percentage_change
                  ) +
                  '%)'
                : '--'}
            </span>
          ),
        },
      ]
    : [
        {
          label: 'Total Balance',
          content: '--',
          truncate: true,
        },
        { label: 'Liquid Balance', content: '--' },
        { label: 'Staked Balance', content: '--' },
        {
          label: '24 hour change',
          content: '--',
        },
        {
          label: 'Week Change',
          content: '--',
        },
        {
          label: 'Month Change',
          content: '--',
        },
        {
          label: 'YTD Change',
          content: '--',
        },
      ];

  return (
    <>
      <BrandBand id="brand-band-lightning-blue" className="slds-p-around_small">
        <div className="">
          <PageHeader
            details={details}
            icon={
              <Icon
                assistiveText={{ label: 'User' }}
                category="standard"
                name="catalog"
                containerStyle={{ backgroundColor: '#48C3CC' }}
              />
            }
            label={
              <a key="trading-wallet-header" href="/defi/accounts">
                Accounts
              </a>
            }
            title="Trading Wallet"
            variant="record-home"
          />
        </div>
        {/* ------------------------Chain-------------------------------------- */}
        <div className="">
          {/* ------------------Tabs------------------------------------ */}
          <div className="accounts_individual">
            <Tabs
              id="tabs-example-default"
              selectedIndex={activeKey}
              onSelect={(k) => setActiveTab(k)}
            >
              <TabsPanel label="Overview">
                {' '}
                <Overview navigatedTab={handleNavigation} account={account} />{' '}
              </TabsPanel>
              <TabsPanel label="Assets">
                {account && tabHierarchy[activeKey] === 'assets' ? (
                  <Assets account={account} />
                ) : (
                  'loading...'
                )}
              </TabsPanel>
              <TabsPanel label="Protocols">
                {account && tabHierarchy[activeKey] === 'protocol' ? (
                  <Protocol account={account} />
                ) : (
                  'loading...'
                )}
              </TabsPanel>
              <TabsPanel label="Transaction History">
                {/* Try to know what you are doing before you begin coding @everyone,
                if I see nonesense from next time i wii see the commit and take your class*/}
                {account && tabHierarchy[activeKey] === 'transactions' ? (
                  <TransactionHistory account={account} />
                ) : (
                  'loading'
                )}
              </TabsPanel>
              <TabsPanel label="Chains">
                {account && tabHierarchy[activeKey] === 'chains' ? (
                  <Chain account={account} />
                ) : (
                  'loading...'
                )}
              </TabsPanel>
              {/* <TabsPanel label="Snapshots">
                {account && tabHierarchy[activeKey] === 'snapshots' ? <Snapshot /> : 'loading...'}
              </TabsPanel> */}
              <TabsPanel label="Details">
                {account && tabHierarchy[activeKey] === 'details' ? (
                  <DetailsTab account={account} />
                ) : (
                  'loading...'
                )}
              </TabsPanel>
            </Tabs>
          </div>
        </div>
      </BrandBand>
    </>
  );
}
